/**
 * Will It Fit Popup Functionality
 * Shows compatible models for a product when "Will it fit?" link is clicked
 */

(function() {
  'use strict';

  console.log('🔍 Will It Fit: Initializing...');

  // Configuration
  const CONFIG = {
    popupOverlayClass: 'will-it-fit-overlay',
    popupClass: 'will-it-fit-popup',
    animationDuration: 300
  };

  // Global variables
  let popupElement = null;
  let overlayElement = null;

  /**
   * Format will_it_fit data into an array of compatible models
   */
  function formatWillItFitData(willItFitData) {
    if (!willItFitData) {
      return [];
    }
    
    // Split by newlines or commas and clean up each item
    const lines = willItFitData.split(/[\n\r,]+/).map(line => line.trim()).filter(line => line.length > 0);
    
    const compatibleModels = [];
    lines.forEach(line => {
      // Remove leading dashes or hyphens
      let cleanLine = line.replace(/^[-–—]+/, '').trim();
      
      // Remove common prefixes like "Compatible Model" or "Compatible Mode"
      cleanLine = cleanLine.replace(/^(Compatible\s+Model\s*[-:]?\s*)/i, '');
      cleanLine = cleanLine.replace(/^(Compatible\s+Mode\s*[-:]?\s*)/i, '');
      
      if (cleanLine.length > 0) {
        compatibleModels.push(cleanLine.toUpperCase());
      }
    });
    
    return compatibleModels;
  }

  /**
   * Extract compatible models from product tags
   */
  function extractCompatibleModelsFromTags(productTagsString) {
    if (!productTagsString) {
      return [];
    }
    
    const tags = productTagsString.split('|').map(tag => tag.trim()).filter(tag => tag.length > 0);
    const compatibleModels = [];
    
    tags.forEach(tag => {
      // Check if tag starts with "compatible_model_"
      if (tag.toLowerCase().startsWith('compatible_model_')) {
        const modelCode = tag.substring('compatible_model_'.length).trim();
        if (modelCode) {
          compatibleModels.push(modelCode.toUpperCase());
        }
      }
    });
    
    return compatibleModels;
  }

  /**
   * Fetch product metafields to get will_it_fit data
   */
  async function fetchProductWillItFitData(productId) {
    try {
      // Try to get product handle from current page
      const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
      if (!productHandle) {
        return null;
      }

      // Use GraphQL to fetch will_it_fit metafield
      const storefrontAccessToken = "7fa16a15dabebca5b20d192fbb1d3087";
      const shopDomain = window.location.hostname;
      
      const PRODUCT_QUERY = `
        query getProduct($handle: String!) {
          product(handle: $handle) {
            metafields(identifiers: [
              {namespace: "custom", key: "will_it_fit"}
            ]) {
              namespace
              key
              value
              type
            }
          }
        }
      `;

      const response = await fetch(`https://${shopDomain}/api/2025-01/graphql.json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": storefrontAccessToken
        },
        body: JSON.stringify({
          query: PRODUCT_QUERY,
          variables: { handle: productHandle }
        })
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      if (result.errors || !result.data?.product) {
        return null;
      }

      const metafields = result.data.product.metafields || [];
      for (let metafield of metafields) {
        if (metafield && metafield.namespace === 'custom' && metafield.key === 'will_it_fit') {
          return metafield.value;
        }
      }
    } catch (error) {
      console.error('🔍 Will It Fit: Error fetching metafields:', error);
    }
    
    return null;
  }

  /**
   * Create popup HTML
   */
  function createPopupHTML(productTitle, compatibleModels) {
    const modelsHTML = compatibleModels.length > 0
      ? compatibleModels.map(model => `<span class="will-it-fit-tag">${model}</span>`).join('')
      : '<p class="will-it-fit-no-models">No compatible models found for this product.</p>';

    return `
      <div class="${CONFIG.popupOverlayClass}">
        <div class="${CONFIG.popupClass}">
          <div class="will-it-fit-header">
            <div class="will-it-fit-title">
              <span class="will-it-fit-icon">
                <img src="https://cdn.shopify.com/s/files/1/0359/1356/4293/files/search.png?v=1759762157" style="width: 16px; height: 16px;">
              </span>
              Will It Fit?
            </div>
            <button class="will-it-fit-close" onclick="closeWillItFitPopup()">&times;</button>
          </div>
          
          <div class="will-it-fit-content">
            <div class="will-it-fit-product-title">
              <h3>${productTitle}</h3>
            </div>
            
            <div class="will-it-fit-models-section">
              <p class="will-it-fit-models-label">Fitting model numbers:</p>
              <div class="will-it-fit-tags">
                ${modelsHTML}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Add popup styles
   */
  function addPopupStyles() {
    if (document.getElementById('will-it-fit-popup-styles')) return;

    const styles = `
      <style id="will-it-fit-popup-styles">
        .will-it-fit-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .will-it-fit-overlay.show {
          opacity: 1;
        }

        .will-it-fit-popup {
          background: white;
          border-radius: 8px;
          max-width: 600px;
          width: 90%;
          max-height: 80vh;
          overflow-y: auto;
          transform: scale(0.8);
          transition: transform 0.3s ease;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .will-it-fit-overlay.show .will-it-fit-popup {
          transform: scale(1);
        }

        .will-it-fit-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 22px;
          border-bottom: 1px solid #e5e5e5;
        }

        .will-it-fit-title {
          display: flex;
          align-items: center;
          font-size: 18px;
          font-weight: bold;
          color: #333;
        }

        .will-it-fit-icon {
          margin-right: 8px;
        }

        .will-it-fit-close {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .will-it-fit-close:hover {
          color: #333;
        }

        .will-it-fit-content {
          padding: 20px;
        }

        .will-it-fit-product-title h3 {
          font-size: 16px;
          font-weight: bold;
          margin: 0 0 20px 0;
          color: #333;
        }

        .will-it-fit-models-section {
          margin-top: 20px;
        }

        .will-it-fit-models-label {
          font-family: 'Avenir Next Medium';
          font-weight: 500;
          font-size: 13px;
          line-height: 21.2px;
          color: #373737;
          margin-bottom: 10px;
        }

        .will-it-fit-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .will-it-fit-tag {
          font-family: 'Avenir Next Medium';
          font-weight: 500;
          font-size: 13px;
          line-height: 21.2px;
          color: #373737;
          padding: 4px 8px;
          background: #f5f5f5;
          border-radius: 4px;
        }

        .will-it-fit-no-models {
          color: #666;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .will-it-fit-popup {
            width: 95%;
            margin: 20px;
          }
        }
      </style>
    `;

    document.head.insertAdjacentHTML('beforeend', styles);
  }

  /**
   * Show popup with animation
   */
  function showPopupAnimation() {
    setTimeout(() => {
      if (overlayElement) {
        overlayElement.classList.add('show');
      }
    }, 10);
  }

  /**
   * Remove existing popup
   */
  function removeExistingPopup() {
    if (popupElement) {
      popupElement.remove();
      popupElement = null;
    }
    if (overlayElement) {
      overlayElement.remove();
      overlayElement = null;
    }
  }

  /**
   * Close popup
   */
  window.closeWillItFitPopup = function() {
    if (overlayElement) {
      overlayElement.classList.remove('show');
      setTimeout(() => {
        removeExistingPopup();
      }, CONFIG.animationDuration);
    }
  };

  /**
   * Add popup event listeners
   */
  function addPopupEventListeners() {
    // Close button
    const closeButton = document.querySelector('.will-it-fit-close');
    if (closeButton) {
      closeButton.addEventListener('click', window.closeWillItFitPopup);
    }

    // Overlay click to close
    if (overlayElement) {
      overlayElement.addEventListener('click', (e) => {
        if (e.target === overlayElement) {
          window.closeWillItFitPopup();
        }
      });
    }

    // Escape key to close
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && popupElement) {
        window.closeWillItFitPopup();
        document.removeEventListener('keydown', handleEscapeKey);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
  }

  /**
   * Show will it fit popup
   */
  async function showWillItFitPopup(blockId) {
    const dataElement = document.getElementById(`will-it-fit-data-${blockId}`);
    if (!dataElement) {
      console.error('🔍 Will It Fit: Data element not found for block', blockId);
      return;
    }

    const productTitle = dataElement.getAttribute('data-product-title') || 'Product';
    const productTags = dataElement.getAttribute('data-product-tags') || '';
    const productId = dataElement.getAttribute('data-product-id');
    const showCompatibleModels = dataElement.getAttribute('data-show-compatible-models') === 'true';

    let compatibleModels = [];

    // Try to get will_it_fit metafield
    if (productId) {
      const willItFitData = await fetchProductWillItFitData(productId);
      if (willItFitData) {
        compatibleModels = formatWillItFitData(willItFitData);
      }
    }

    // If no metafield data, extract from product tags
    if (compatibleModels.length === 0 && productTags) {
      compatibleModels = extractCompatibleModelsFromTags(productTags);
    }

    // Create and show popup
    const popupHTML = createPopupHTML(productTitle, compatibleModels);
    
    // Remove existing popup if any
    removeExistingPopup();
    
    // Add popup to DOM
    document.body.insertAdjacentHTML('beforeend', popupHTML);
    
    // Get popup elements
    popupElement = document.querySelector(`.${CONFIG.popupClass}`);
    overlayElement = document.querySelector(`.${CONFIG.popupOverlayClass}`);
    
    // Add styles
    addPopupStyles();
    
    // Show popup with animation
    showPopupAnimation();
    
    // Add event listeners
    addPopupEventListeners();
  }

  /**
   * Scroll to description and expand it
   */
  function scrollToDescriptionAndExpand() {
    // Find the product description section
    const descriptionSection = document.querySelector('.product-block-list__item--description');
    
    if (!descriptionSection) {
      console.warn('🔍 Will It Fit: Description section not found');
      return;
    }

    // Find the expandable content and "View more" button
    const expandableContent = descriptionSection.querySelector('.expandable-content');
    const viewMoreButton = descriptionSection.querySelector('.expandable-content__toggle');
    
    // Calculate scroll position (account for header)
    let headerHeight = 0;
    try {
      const header = document.querySelector('.shopify-section__header') || document.querySelector('header');
      if (header && header.offsetHeight) {
        headerHeight = header.offsetHeight;
      }
    } catch (error) {
      // If there's an error getting header height, use CSS variable or default
      try {
        const headerHeightVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
        if (headerHeightVar) {
          headerHeight = parseInt(headerHeightVar) || 0;
        }
      } catch (e) {
        headerHeight = 100; // Default fallback
      }
    }
    
    const extraOffset = 30;
    const position = descriptionSection.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;

    // Scroll to description section
    window.scrollTo({ 
      top: Math.max(0, position), // Ensure position is not negative
      behavior: 'smooth' 
    });

    // If expandable content and button exist, click it after scroll
    if (expandableContent && viewMoreButton) {
      // Check if already expanded
      const isExpanded = expandableContent.getAttribute('aria-expanded') === 'true';
      
      // If not expanded, click the "View more" button after scroll completes
      if (!isExpanded) {
        // Wait for scroll to complete, then click the button
        setTimeout(() => {
          // Double-check it's still not expanded (in case user clicked it manually)
          if (viewMoreButton && expandableContent.getAttribute('aria-expanded') !== 'true') {
            try {
              viewMoreButton.click();
            } catch (error) {
              console.error('🔍 Will It Fit: Error clicking View more button:', error);
            }
          }
        }, 600); // Wait 600ms for smooth scroll to complete
      }
    } else {
      console.warn('🔍 Will It Fit: Expandable content or View more button not found, only scrolling to description');
    }
  }

  /**
   * Initialize will it fit functionality
   */
  function init() {
    // Listen for clicks on will-it-fit links
    document.addEventListener('click', function(e) {
      const link = e.target.closest('.will-it-fit-link');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();

      // Get block ID from link ID or parent
      const linkId = link.id;
      const blockIdMatch = linkId.match(/will-it-fit-link-(.+)/);
      const blockId = blockIdMatch ? blockIdMatch[1] : 'default';

      console.log('🔍 Will It Fit: Link clicked for block', blockId);
      
      // Instead of showing popup, scroll to description and expand it
      scrollToDescriptionAndExpand();
    }, true); // Use capture phase

    console.log('🔍 Will It Fit: Initialization complete!');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

